/**
 * Prompts that teach a model to write code for Genie's shader runtime.
 *
 * Every uniform name, texture layout and `window.genie` field is read from the
 * media contract, so the prompt cannot drift from what the runtime binds. The
 * compact variant exists for the Genie credits route, which rejects system
 * prompts over 4000 characters.
 */

import type { RuntimeMode } from '../types'
import {
  GENIE_JS_API,
  GLSL_AUDIO_TEXTURE,
  GLSL_BUILTIN_UNIFORMS,
  GLSL_INPUT_UNIFORMS,
  GLSL_MODEL_TEXTURE,
  GLSL_TEXT_TEXTURE,
  SHADER_INPUT_TYPES,
} from '../media/contract'
import type { ShaderInputType } from '../media/contract'
import type { ShaderGenerationRequest } from './types'

/** The credits route rejects longer system prompts; this leaves headroom under its 4000. */
export const COMPACT_SYSTEM_PROMPT_LIMIT = 3800

const B = GLSL_BUILTIN_UNIFORMS
const I = GLSL_INPUT_UNIFORMS

// ---------------------------------------------------------------------------
// System prompt
// ---------------------------------------------------------------------------

function envelope(): string {
  return `Answer in exactly this format, nothing else:
TITLE: <2-4 words>
RUNTIME: glsl | p5 | threejs | raw
INPUTS: <comma-separated subset of ${SHADER_INPUT_TYPES.join(', ')} - or none>
SUMMARY: <one sentence>
then ONE fenced code block (\`\`\`glsl or \`\`\`js) holding the complete program.`
}

function glslUniformLines(compact: boolean): string {
  const builtins = compact
    ? [`float ${B.time} (seconds), vec2 ${B.resolution} (px), vec2 ${B.mouse} (px, origin bottom-left), float ${B.amplitude} (audio RMS 0..1)`]
    : [
        `uniform float ${B.time}; // seconds`,
        `uniform vec2 ${B.resolution}; // canvas pixels`,
        `uniform vec2 ${B.mouse}; // pixels, origin bottom-left`,
        `uniform float ${B.amplitude}; // audio RMS 0..1`,
      ]
  const inputs = SHADER_INPUT_TYPES.map((type) => {
    const u = I[type]
    if (compact) return `${type}: sampler2D ${u.sampler}, float ${u.has}${u.resolution ? `, vec2 ${u.resolution}` : ''}`
    return `uniform sampler2D ${u.sampler}; uniform float ${u.has};${u.resolution ? ` uniform vec2 ${u.resolution};` : ''} // ${type}`
  })
  const has = compact
    ? `u_hasX is 1.0 while connected, else 0.0. Textures are upright; never flip y.`
    : `u_hasX is 1.0 while that input is connected, else 0.0 - fall back gracefully with it. Image, video, text and 3D textures are upright: sample them at v_texCoord (or gl_FragCoord.xy / u_resolution) and never flip y.`
  const audio = `${I.audio.sampler}: ${GLSL_AUDIO_TEXTURE.width}x${GLSL_AUDIO_TEXTURE.height}. y=${GLSL_AUDIO_TEXTURE.fftRow} is FFT 0..1 (bass at x=0), y=${GLSL_AUDIO_TEXTURE.waveformRow} is waveform (0.5 = silence).`
  const depth = compact ? 'a = depth (1 near, 0 off the mesh)' : 'a = depth (1 near), a = 0 off the mesh'
  const model = `${I['3d'].sampler}: the 3D model as a ${GLSL_MODEL_TEXTURE.turntableSecondsPerTurn}s turntable filling the canvas; rgb = view normal*0.5+0.5, ${depth}.`
  const text = `${I.text.sampler}: the connected text, white bold glyphs on transparent black, centred and wrapped to fill ${Math.round(GLSL_TEXT_TEXTURE.fill * 100)}% of the canvas; use texture2D(${I.text.sampler}, v_texCoord).a as a mask.`
  return [...builtins, ...inputs, has, audio, model, ...(compact ? [] : [text])].join('\n')
}

function runtimeRules(compact: boolean): string {
  const quad = compact ? '' : ' on a fullscreen quad'
  const glsl = `glsl - a WebGL1 fragment shader (GLSL ES 1.00)${quad}. Start with "precision mediump float;". UV 0..1 is "varying vec2 v_texCoord;". Write gl_FragColor, sample with texture2D. Never "#version 300 es", "out vec4" or texture(). Loop bounds must be constants; use float literals (1.0, not 1). Declare only the uniforms you use.`
  const p5 = `p5 - p5.js global mode: function setup() { createCanvas(genie.width, genie.height); } and function draw(). No preload, no loadFont.`
  const addons = compact ? '' : ' (no OrbitControls, no GLTFLoader)'
  const three = `threejs - THREE is the global UMD build (r160); no imports, no addons${addons}. Render with new THREE.WebGLRenderer({ canvas: genie.canvas }) at genie.width x genie.height and drive your own requestAnimationFrame loop.`
  const raw = `raw - plain JavaScript on genie.canvas (getContext('2d') or 'webgl') with your own requestAnimationFrame loop.`
  const extra = compact
    ? ''
    : `\nPick glsl for per-pixel effects on images, video, audio and generative fields; p5 for 2D drawing, particles and text; threejs for 3D scenes and the 3D model input; raw only when nothing else fits.
In JS runtimes inputs can arrive or change after start: read them every frame or subscribe with genie.onInput, and handle null. Load an image with loadImage(genie.inputImage, cb) in p5 or new THREE.TextureLoader().load(genie.inputImage) in three.js. Draw video in p5 with image(genie.inputVideo, 0, 0, width, height), in raw with ctx.drawImage(genie.inputVideo, ...), in three.js with new THREE.VideoTexture(genie.inputVideo).`
  return `Runtimes:\n${glsl}\n${p5}\n${three}\n${raw}${extra}`
}

function paramRules(compact: boolean): string {
  const int = compact ? '' : '\n// @param int count 1 200 50 Count'
  const base = `Controls: add a "// @param" line per tweakable value, at the top:
// @param float speed 0 5 1 Speed${int}
// @param bool mirror false Mirror
// @param color tint #ff8800 Tint
// @param select mode add,multiply,screen add Blend
Form: type name min max default label (bool/color: name default label; select: name options default label).
GLSL: declare a same-named uniform (float, int, bool; color is vec3; select is a float index). JS: read genie.params.name every frame. Never name a param u_*.`
  return compact ? base : `${base}\nOffer 2-6 meaningful controls; defaults must look good.`
}

function sandboxRules(compact: boolean): string {
  if (compact) {
    return `No network in the sandbox: no fetch, URLs, imports, extra libraries or external assets - only the connected inputs. Always animate; look good when an input is missing.`
  }
  return `The code runs in a sandboxed iframe with no network: no fetch, no URLs, no imports, no extra libraries, no external images, fonts or textures. The only media is the connected inputs. Always animate over time, never block the main thread, and keep the output good-looking when an input is missing. Do not use localStorage, alert or prompt. Do not explain the code outside the SUMMARY line.`
}

/**
 * The system prompt for shader authoring. `compact` stays within
 * `COMPACT_SYSTEM_PROMPT_LIMIT` characters.
 */
export function buildShaderSystemPrompt(options: { compact: boolean }): string {
  const { compact } = options
  const intro = compact
    ? `You write code for Genie's Shader node, a live visual in a browser sandbox.`
    : `You write code for Genie's Shader node: a live, animated visual that runs in a sandboxed browser iframe. Users describe an effect, often acting on media they plugged into the node (image, video, audio, text, 3D model), and you write the complete program.`
  return [
    intro,
    envelope(),
    runtimeRules(compact),
    `GLSL uniforms the runtime provides:\n${glslUniformLines(compact)}`,
    `JS runtimes (p5, threejs, raw) get window.genie:\n${GENIE_JS_API}`,
    paramRules(compact),
    sandboxRules(compact),
  ].join('\n\n')
}

// ---------------------------------------------------------------------------
// User prompts
// ---------------------------------------------------------------------------

const FENCE_LANGUAGE: Record<RuntimeMode, string> = { glsl: 'glsl', p5: 'js', threejs: 'js', raw: 'js' }

function inputList(types: readonly ShaderInputType[]): string {
  return types.length ? types.join(', ') : 'none'
}

function connectedLine(connected: readonly ShaderInputType[]): string {
  return connected.length
    ? `The user has plugged in: ${inputList(connected)} - the effect must use them.`
    : 'No media is plugged in. Make it self-contained unless the request needs an input; declare any input you use.'
}

/** The user turn for a create or edit request. */
export function buildShaderUserPrompt(request: ShaderGenerationRequest): string {
  const current = request.mode === 'edit' ? request.current : undefined
  if (!current) {
    const runtime = request.preferredRuntime
      ? `Preferred runtime: ${request.preferredRuntime} (use another only if the effect needs it).`
      : 'Pick the runtime that suits the effect best.'
    return [`Create a new shader.`, `Request: ${request.prompt.trim()}`, connectedLine(request.connectedInputs), runtime].join('\n')
  }
  return [
    `Edit the current shader.`,
    `Request: ${request.prompt.trim()}`,
    connectedLine(request.connectedInputs),
    `Current runtime: ${current.runtimeMode}. Current inputs: ${inputList(current.inputTypes)}.`,
    `Current code:`,
    `\`\`\`${FENCE_LANGUAGE[current.runtimeMode]}\n${current.code.trim()}\n\`\`\``,
    `Return the COMPLETE updated program in the same format - never a diff or a fragment. Keep the runtime, the existing // @param controls and the inputs unless the request asks to change them. Change only what the request asks for.`,
  ].join('\n')
}

/** Follow-up turn after the runtime rejected the model's code. */
export function buildRepairPrompt(previous: { code: string; runtimeMode: RuntimeMode }, error: { message: string; line?: number }): string {
  const where = error.line ? ` (line ${error.line})` : ''
  return [
    `That ${previous.runtimeMode} program failed in Genie's runtime${where}:`,
    error.message.trim(),
    `The code that failed:`,
    `\`\`\`${FENCE_LANGUAGE[previous.runtimeMode]}\n${previous.code.trim()}\n\`\`\``,
    `Fix the error and answer again in the same format with the COMPLETE corrected program. Keep the same effect, runtime and controls.`,
  ].join('\n')
}
